/**
 * Filtry synchronizované do URL.
 *
 * Akceptační kritérium 08 §2: „Deep-link libovolného filtrovaného pohledu
 * funguje po refreshi." Proto je JEDINÝM zdrojem pravdy `useSearchParams`,
 * ne lokální stav — po F5 se pohled poskládá zpátky z URL.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { GlobalFilters, Phase, Supplier } from '@/api/models';
import { presetRange, type CompareMode } from '@/lib/dates';

const DEFAULT_PRESET = '7d';

function readList(params: URLSearchParams, key: string): string[] {
  const values = params.getAll(key);
  if (values.length === 0) return [];
  // Podpora obou zápisů: ?domain=a&domain=b i ?domain=a,b
  return values.flatMap((v) => v.split(',')).filter(Boolean);
}

export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<GlobalFilters>(() => {
    const fallback = presetRange(DEFAULT_PRESET);
    const compare = searchParams.get('compare');
    return {
      date_from: searchParams.get('from') ?? fallback.from,
      date_to: searchParams.get('to') ?? fallback.to,
      compare:
        compare === 'prev_period' || compare === 'prev_week' ? (compare as CompareMode) : 'none',
      domain: readList(searchParams, 'domain'),
      type: readList(searchParams, 'type'),
      supplier: readList(searchParams, 'supplier') as Supplier[],
      phase: readList(searchParams, 'phase') as Phase[],
      channel_tag: readList(searchParams, 'channel'),
      placement_ids: readList(searchParams, 'pid'),
    };
  }, [searchParams]);

  const setFilters = useCallback(
    (patch: Partial<GlobalFilters>, options?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          const setScalar = (key: string, value: string | undefined, omitWhen?: string) => {
            if (value === undefined || value === '' || value === omitWhen) next.delete(key);
            else next.set(key, value);
          };
          const setList = (key: string, values: string[] | undefined) => {
            next.delete(key);
            if (!values) return;
            for (const value of values) next.append(key, value);
          };

          if ('date_from' in patch) setScalar('from', patch.date_from);
          if ('date_to' in patch) setScalar('to', patch.date_to);
          if ('compare' in patch) setScalar('compare', patch.compare, 'none');
          if ('domain' in patch) setList('domain', patch.domain);
          if ('type' in patch) setList('type', patch.type);
          if ('supplier' in patch) setList('supplier', patch.supplier);
          if ('phase' in patch) setList('phase', patch.phase);
          if ('channel_tag' in patch) setList('channel', patch.channel_tag);
          if ('placement_ids' in patch) setList('pid', patch.placement_ids);

          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of ['from', 'to', 'compare', 'domain', 'type', 'supplier', 'phase', 'channel', 'pid']) {
          next.delete(key);
        }
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const activeCount = useMemo(() => {
    return (
      filters.domain.length +
      filters.type.length +
      filters.supplier.length +
      filters.phase.length +
      filters.channel_tag.length +
      filters.placement_ids.length +
      (filters.compare !== 'none' ? 1 : 0)
    );
  }, [filters]);

  return { filters, setFilters, resetFilters, activeCount };
}

/**
 * Pomocník pro stránkové parametry mimo globální filtry (aktivní záložka,
 * přepínač artefaktů…). Taky jde do URL, aby byl deep-link úplný.
 */
export function useUrlParam(key: string, fallback: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? fallback;

  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === fallback) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true },
      );
    },
    [key, fallback, setSearchParams],
  );

  return [value, setValue] as const;
}
